import React from 'react'
import { useIntl } from 'react-intl'

import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'
import { ILocationFilterSource, locationOptionsByCountry } from '@vacationtracker/shared/functions/location-country-groups'

import { FilterTriggerRow, FilterRowItem, FilterValues, useFilterRowLabels, buildLabelsFilterRowItem } from '../../../../design-system/FilterTrigger'
import { FilterDimension } from '../hooks/useUsersFilters'
import { IUsersFilter } from '../types'

interface IFilterOption {
  id: string
  name: string
  level?: number
}

export interface IUsersFilterBarProps {
  filters: IUsersFilter
  statusOptions: IFilterOption[]
  locations: ILocationFilterSource[]
  departments: IFilterOption[]
  labels: IFilterOption[]
  labelsEnabled: boolean
  isScopeLocked: boolean
  hasActiveFilters: boolean
  onStatusChange: (status: string) => void
  onDimensionChange: (dimension: FilterDimension, values: string[]) => void
  onClearAll: () => void
  inviteStatuses?: EmailInviteStatusEnum[]
  inviteStatusOptions?: IFilterOption[]
  onInviteStatusesChange?: (values: EmailInviteStatusEnum[]) => void
  /** false keeps inline triggers at every width - for hosts like the export modal where the
   * bottom-sheet drawer can't stack on the surface it lives in.
   * @default true */
  collapseOnMobile?: boolean
}

const toFilterOptions = (options: IFilterOption[]) => options.map((option) => ({ value: option.id, label: option.name, indentLevel: option.level }))

const sameValues = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((entry, index) => entry === b[index])

export const UsersFilterBar = (props: IUsersFilterBarProps): React.ReactElement => {
  const {
    filters,
    statusOptions,
    locations,
    departments,
    labels,
    labelsEnabled,
    isScopeLocked,
    hasActiveFilters,
    onStatusChange,
    onDimensionChange,
    onClearAll,
    inviteStatuses,
    inviteStatusOptions,
    onInviteStatusesChange,
    collapseOnMobile,
  } = props
  const { formatMessage } = useIntl()
  const rowLabels = useFilterRowLabels()

  const clearLabel = (filterLabel: string) => formatMessage({ id: 'users.table.clearFilter' }, { filter: filterLabel })
  const statusLabel = formatMessage({ id: 'app.status' })
  const locationsLabel = formatMessage({ id: 'app.locations' })
  const departmentsLabel = formatMessage({ id: 'app.departments' })
  const labelsLabel = formatMessage({ id: 'app.labels' })
  const invitesLabel = formatMessage({ id: 'users.table.inviteFilterLabel' })
  const scopeLocked = isScopeLocked ? formatMessage({ id: 'users.table.scopeLocked' }) : undefined
  const locationsByCountry = locationOptionsByCountry(locations, formatMessage({ id: 'locationPlace.card.noCountrySet' }))

  const configs: FilterRowItem[] = [
    { id: 'status', label: statusLabel, options: toFilterOptions(statusOptions), multiple: false, clearable: false, keepOrder: true },
    {
      id: 'locations',
      label: locationsLabel,
      options: locationsByCountry ?? toFilterOptions(locations),
      keepOrder: Boolean(locationsByCountry),
      lockReason: scopeLocked,
      clearLabel: clearLabel(locationsLabel),
    },
    { id: 'departments', label: departmentsLabel, options: toFilterOptions(departments), lockReason: scopeLocked, clearLabel: clearLabel(departmentsLabel) },
    buildLabelsFilterRowItem({ labels, labelsEnabled, formatMessage, clearLabel: clearLabel(labelsLabel) }),
    ...(inviteStatusOptions && onInviteStatusesChange
      ? [{ id: 'invites', label: invitesLabel, options: toFilterOptions(inviteStatusOptions), clearLabel: clearLabel(invitesLabel), keepOrder: true } as FilterRowItem]
      : []),
  ]

  const values: FilterValues = {
    status: [filters.status],
    locations: filters.locationIds || [],
    departments: filters.teamIds || [],
    labels: filters.labelIds || [],
    invites: inviteStatuses || [],
  }

  const handleChange = (next: FilterValues) => {
    if (!sameValues(values.status, next.status)) {
      onStatusChange(next.status?.[0] || 'ACTIVE')
    }
    if (!sameValues(values.locations, next.locations)) {
      onDimensionChange('Locations', next.locations || [])
    }
    if (!sameValues(values.departments, next.departments)) {
      onDimensionChange('Departments', next.departments || [])
    }
    if (!sameValues(values.labels, next.labels)) {
      onDimensionChange('Labels', next.labels || [])
    }
    if (onInviteStatusesChange && !sameValues(values.invites, next.invites)) {
      onInviteStatusesChange((next.invites || []) as EmailInviteStatusEnum[])
    }
  }

  return (
    <div className="vt-users-filterbar">
      <FilterTriggerRow
        configs={configs}
        values={values}
        onChange={handleChange}
        visibleLimit={configs.length}
        collapseOnMobile={collapseOnMobile}
        showClearAll={hasActiveFilters}
        onClearAll={onClearAll}
        labels={rowLabels}
      />
    </div>
  )
}
